import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useLocale } from 'next-intl';
import { useRouter } from 'next/router';
import Select from '@components/atoms/select/select';
import Headline from '@components/atoms/headline/headline';
import DateSelect from '@components/molecules/date-select/dateSelect';
import moment from 'moment';
import StyledFilterSettings from './filter-settings.style';
import Container from '../../../../layout/container/container';
import getStartDate from './helpers/getStartDate';

const FilterSettings = ({ calendar, onSelectionChange, results }) => {
  const [activeDates, setActiveDates] = useState([]);
  const router = useRouter();
  const locale = useLocale();

  console.log(calendar);

  const [selection, setSelection] = useState({
    browse: ['events,festivals,guidedTours'],
    type: '',
    date: getStartDate(),
    gomusGrades: '',
    language: '',
    tourTopics: [],
    eventTopics: []
  });

  const mdFilter = {
    language: {
      handle: 'language',
      mapping: {
        9518330: '1',
        9518331: '3',
        11627339: '4',
        11627341: '7'
      }
    },
    gomusGrades: {
      handle: 'schoolGrade',
      mapping: {
        18878: '1',
        18879: '2',
        18880: '3',
        18881: '4',
        18882: '5',
        18883: '6',
        18884: '7',
        18885: '8',
        18886: '9',
        18887: '10',
        18888: '11',
        18889: '12',
        18890: '13',
        14: '14'
      }
    },
    type: {
      handle: 'proposalCategory',
      mapping: {
        12362538: '1', // Schulklasse
        18809: '3', // 'Erwachsene'
        11075752: '7', // Student*innen
        18808: '8', // Kinder
        18811: '0' // 'Familien'
      }
    }
  };

  const TYPE_SCHOOL_CLASS = 12362538;

  const placeholderSelect = locale === 'de' ? 'AUSWÄHLEN' : 'SELECT';

  const addQueryParam = params => {
    router.push({ pathname: router.pathname, query: { ...router.query, ...params } }, null, {
      shallow: true
    });
  };

  const getFilterValueForQueryParam = key => {
    const urlParams = new URLSearchParams(window.location.search);
    const handle = mdFilter[key]?.handle;
    if (!handle) {
      return '';
    }

    const queryValue = urlParams.get(handle);
    if (!queryValue) {
      return '';
    }

    const mappedValue = Object.keys(mdFilter[key].mapping).find(
      k => mdFilter[key].mapping[k] === queryValue
    );

    if (!mappedValue) {
      return '';
    }

    return parseInt(mappedValue, 10);
  };

  const onSelection = (value, key) => {
    if (key === 'date') {
      const formattedDate = moment(value).format('YYYY-MM-DD');
      addQueryParam({ dateRequest: formattedDate });
    } else if (key === 'type') {
      const mdFilterKey = mdFilter[key]?.handle;
      const mdFilterValue = mdFilter[key]?.mapping[value];

      if (mdFilterValue !== TYPE_SCHOOL_CLASS) {
        addQueryParam({
          schoolGrade: '',
          [mdFilterKey]: mdFilterValue
        });
        setSelection(prev => ({ ...prev, gomusGrades: '' }));
      } else {
        addQueryParam({
          [mdFilterKey]: mdFilterValue
        });
      }
    } else {
      const mdFilterKey = mdFilter[key]?.handle;
      const mdFilterValue = mdFilter[key]?.mapping[value];

      if (mdFilterKey && !mdFilterValue) {
        addQueryParam({ [mdFilterKey]: '' });
      } else if (mdFilterKey && mdFilterValue) {
        addQueryParam({ [mdFilterKey]: mdFilterValue });
      }
    }

    return setSelection(prev => ({ ...prev, [key]: value }));
  };

  const transformCategoriesToSelectOption = (categories, limit) => {
    let filteredCategories = categories;
    // Convert the limit object to an array of its keys if it's provided, otherwise use null
    if (limit) {
      const limitKeys = limit ? Object.keys(limit) : null;

      // Convert the categories object to an array of its values
      const categoriesArray = Object.values(categories);

      // Filter the categories if a limit is provided
      filteredCategories = limitKeys
        ? categoriesArray.filter(category => limitKeys.includes(category.id.toString()))
        : categoriesArray;

      console.log('Filtered Categories:', categoriesArray);
    }

    return filteredCategories.map(category => ({
      text: category.title,
      value: category.id
    }));
  };

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selection);
    }
  }, [selection]);

  useEffect(() => {
    const language = getFilterValueForQueryParam('language');
    const gomusGrades = getFilterValueForQueryParam('gomusGrades');
    const type = getFilterValueForQueryParam('type');

    setSelection(prev => ({
      ...prev,
      language,
      gomusGrades,
      type
    }));
  }, []);

  useEffect(() => {
    const availableDates = Object.values(results?.meta?.availableDates || {}).flatMap(d => d);

    setActiveDates(availableDates.map(date => new Date(date.replace(/-/g, '/'))));
  }, [results]);

  return (
    <StyledFilterSettings>
      <Container>
        <div className="filterSettings__browse">
          <div className="filterSettings__browse__labels">
            <Headline className="filterSettings__browse__headline" tag="h3" primary>
              {calendar?.filter.browse}
            </Headline>
          </div>
        </div>
      </Container>
      <Container className="filterSettings__container">
        <div className="filterSettings__selects">
          <Select
            standalone
            canSelectPlaceholder
            label={calendar?.filter?.type}
            defaultValue={selection.type}
            value={selection.type}
            options={transformCategoriesToSelectOption(
              calendar?.filterSettings?.types,
              mdFilter.type.mapping
            )}
            placeholder={placeholderSelect}
            onChange={e => {
              onSelection(
                e.target.value === placeholderSelect ? '' : Number(e.target.value),
                'type'
              );
            }}
          />
          {selection.type === TYPE_SCHOOL_CLASS && (
            <Select
              standalone
              canSelectPlaceholder
              label={calendar?.filter?.gomusGrades}
              defaultValue={selection.gomusGrades}
              options={transformCategoriesToSelectOption(calendar?.filterSettings?.gomusGrades)}
              placeholder={placeholderSelect}
              onChange={e =>
                onSelection(
                  e.target.value === placeholderSelect ? '' : e.target.value,
                  'gomusGrades'
                )
              }
            />
          )}
          <DateSelect
            label={calendar?.filter?.date}
            locale={locale}
            defaultValue={selection.date}
            onChange={value => onSelection(value, 'date')}
            activeDates={activeDates}
          />
          <Select
            standalone
            canSelectPlaceholder
            label={calendar?.filter?.language}
            defaultValue={selection.language}
            options={transformCategoriesToSelectOption(calendar?.filterSettings?.languages)}
            placeholder={placeholderSelect}
            onChange={e =>
              onSelection(e.target.value === placeholderSelect ? '' : e.target.value, 'language')
            }
          />
        </div>
      </Container>
    </StyledFilterSettings>
  );
};

FilterSettings.propTypes = {
  calendar: PropTypes.shape({
    filter: PropTypes.shape({
      browse: PropTypes.string,
      filter: PropTypes.string,
      type: PropTypes.string,
      date: PropTypes.string,
      language: PropTypes.string,
      tourTopics: PropTypes.string,
      gomusGrades: PropTypes.string,
      eventTopics: PropTypes.string
    }),
    filterSettings: PropTypes.shape({
      browse: PropTypes.arrayOf(PropTypes.shape()),
      types: PropTypes.arrayOf(PropTypes.shape()),
      languages: PropTypes.arrayOf(PropTypes.shape()),
      gomusGrades: PropTypes.arrayOf(PropTypes.shape()),
      tourTopics: PropTypes.arrayOf(PropTypes.shape()),
      eventTopics: PropTypes.arrayOf(PropTypes.shape())
    })
  }),
  onSelectionChange: PropTypes.func,
  results: PropTypes.shape()
};

const mapStateToProps = ({ app: { pageData } }) => ({
  calendar: pageData.calendar
});

export default connect(mapStateToProps)(FilterSettings);
